import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { useMe } from '../lib/me';
import { dateTimeCentral } from '../lib/format';

interface MappingRow {
  rowcal_account: string;
  rowcal_description: string | null;
  allstar_account: string;
  allstar_line: string | null;
  notes: string | null;
  updated_by: string;
  updated_at: string;
}

interface Unmapped {
  rowcal_account: string;
  description: string | null;
  period: string;
}

export default function Mapping() {
  const api = useApi();
  const qc = useQueryClient();
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const [filter, setFilter] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const mapping = useQuery({ queryKey: ['mapping'], queryFn: () => api.get<MappingRow[]>('/api/mapping') });
  const unmapped = useQuery({ queryKey: ['unmapped'], queryFn: () => api.get<Unmapped[]>('/api/uploads/unmapped') });

  const save = useMutation({
    mutationFn: (rows: { rowcal_account: string; rowcal_description: string | null; allstar_account: string }[]) =>
      api.put('/api/mapping', rows),
    onSuccess: () => {
      setDrafts({});
      qc.invalidateQueries({ queryKey: ['mapping'] });
      qc.invalidateQueries({ queryKey: ['unmapped'] });
    },
  });

  const rows = useMemo(() => {
    const f = filter.toLowerCase();
    return (mapping.data ?? []).filter(
      (r) => !f || r.rowcal_account.toLowerCase().includes(f) || (r.rowcal_description ?? '').toLowerCase().includes(f)
        || r.allstar_account.includes(f) || (r.allstar_line ?? '').toLowerCase().includes(f),
    );
  }, [mapping.data, filter]);

  const pending = Object.entries(drafts).filter(([, v]) => /^\d{5}$/.test(v));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl text-denim">GL Mapping Key</h1>
        <p className="text-sm text-allstar-gray-dark mt-1">
          Each Rowcal GL maps to one Allstar Sage account, which rolls into the same statement lines the Reporting Hub uses.
        </p>
      </div>

      {!!unmapped.data?.length && (
        <div className="card p-5 border-l-4 border-l-caution">
          <h2 className="font-semibold text-denim">Unmapped Rowcal accounts ({unmapped.data.length})</h2>
          <p className="text-sm text-allstar-gray-dark">From accepted uploads. Outputs can't be generated for a period until these are mapped.</p>
          <table className="table-default mt-3">
            <thead><tr><th>Rowcal GL</th><th>Description</th><th>First seen</th><th>Allstar account</th></tr></thead>
            <tbody>
              {unmapped.data.map((u) => (
                <tr key={u.rowcal_account}>
                  <td className="font-mono">{u.rowcal_account}</td>
                  <td>{u.description}</td>
                  <td>{u.period}</td>
                  <td>
                    <input className="border border-allstar-gray rounded px-2 py-1 w-24 font-mono" placeholder="5xxxx"
                      disabled={!isAdmin} value={drafts[u.rowcal_account] ?? ''}
                      onChange={(e) => setDrafts({ ...drafts, [u.rowcal_account]: e.target.value.trim() })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isAdmin && (
            <button className="btn-primary mt-3" disabled={!pending.length || save.isPending}
              onClick={() => save.mutate(pending.map(([acct, allstar]) => ({
                rowcal_account: acct,
                rowcal_description: unmapped.data!.find((u) => u.rowcal_account === acct)?.description ?? null,
                allstar_account: allstar,
              })))}>
              Save {pending.length || ''} mapping{pending.length === 1 ? '' : 's'}
            </button>
          )}
          {save.error && <div className="text-negative text-sm mt-2">{(save.error as Error).message}</div>}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="p-3 border-b border-allstar-gray">
          <input className="border border-allstar-gray rounded px-3 py-1.5 w-80 text-sm" placeholder="Filter by account, description, or line"
            value={filter} onChange={(e) => setFilter(e.target.value)} />
          <span className="ml-3 text-sm text-allstar-gray-dark">{mapping.data?.length ?? 0} mapped accounts</span>
        </div>
        <table className="table-default">
          <thead>
            <tr><th>Rowcal GL</th><th>Rowcal description</th><th>Allstar acct</th><th>Allstar line</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.rowcal_account}>
                <td className="font-mono">{r.rowcal_account}</td>
                <td>{r.rowcal_description}</td>
                <td className="font-mono">{r.allstar_account}</td>
                <td className={r.allstar_line ? '' : 'text-negative'}>{r.allstar_line ?? 'Not a P&L line'}</td>
                <td className="text-xs text-allstar-gray-dark">{r.updated_by} · {dateTimeCentral(r.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
